import { HttpErrorResponse } from '@angular/common/http';
import {
  ErrorHandler,
  Injectable,
  Injector,
  NgZone,
  inject
} from '@angular/core';

import { environment } from 'environments/environment';

import { getRemoteError } from '@app/core/errors/helpers/get-remote-error';
import { LivErrorResponse } from '@app/core/models/response-protocol.model';
import { ToastService } from '@app/core/ui/components/toast';

function isLivErrorResponse(
  error: Error | LivErrorResponse
): error is LivErrorResponse {
  const livErrorResponse = error as LivErrorResponse;

  return (
    Boolean(livErrorResponse?.status) &&
    Boolean(livErrorResponse?.error?.message)
  );
}

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandlerService implements ErrorHandler {
  readonly #injector = inject(Injector);
  readonly #zone = inject(NgZone);

  readonly #alertDomains = this.#extractDomainsFromEnvironment();

  handleError(error: Error | HttpErrorResponse | LivErrorResponse): void {
    if (isLivErrorResponse(error)) {
      this.#handleLivError(error);
      return;
    }

    if (!(error instanceof HttpErrorResponse)) {
      console.error('An unexpected error occurred:', error);
      return;
    }

    if (!this.#shouldShowAlert(error.url || '')) {
      console.warn(
        `Error from ${error.url} does not match configured alert domains. No alert will be shown.`
      );
      return;
    }

    this.#zone.run(() => {
      const toast = this.#injector.get(ToastService);

      const { message } = getRemoteError(error, {
        defaultMessage: this.#getGenericMessage(error.status)
      });

      toast.danger(message);
    });
  }

  #handleLivError(error: LivErrorResponse): void {
    const url = error.error?.stack?.url ?? '';

    if (url && !this.#shouldShowAlert(url)) {
      console.warn(
        `Error from ${url} does not match configured alert domains. No alert will be shown.`
      );
      return;
    }

    this.#zone.run(() => {
      const toast = this.#injector.get(ToastService);
      toast.danger(error.error.message);
    });
  }

  #getGenericMessage(status: number): string {
    if (status >= 500) {
      return 'Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.';
    }

    if (status === 401) {
      return 'Acesso não autorizado. Faça login novamente.';
    }

    if (status === 403) {
      return 'Você não tem permissão para executar esta ação.';
    }

    if (status === 404) {
      return 'Recurso não encontrado.';
    }

    if (status >= 400 && status < 500) {
      return 'Requisição inválida. Verifique os dados e tente novamente.';
    }

    return 'Ocorreu um erro inesperado.';
  }

  #shouldShowAlert(url: string): boolean {
    if (!url) {
      return false;
    }

    try {
      const domain = new URL(url).hostname;
      return this.#alertDomains.some(
        (alertDomain) => domain.endsWith(alertDomain) || domain === alertDomain
      );
    } catch {
      return false;
    }
  }

  #extractDomainsFromEnvironment(): string[] {
    const domains = new Set<string>();

    if (environment.apiUrl) {
      domains.add(this.#extractDomain(environment.apiUrl));
    }

    return Array.from(domains);
  }

  #extractDomain(url: string): string {
    try {
      const domain = url.replace(/(https?:\/\/)?([^\/]+).*/i, '$2');
      return domain;
    } catch {
      return url;
    }
  }
}
